import type { PhaseEnvelope, Timestamp } from '@2k48/shared';
import { renderLoop } from '../game/RenderLoop';
import type { SyncedClock } from './SyncedClock';

/**
 * Scheduling against absolute server timestamps.
 *
 * This is the client-side half of the phase convention, and it is deliberately
 * NOT built on `setTimeout`.
 *
 * `setTimeout(fn, endsAt - now)` computes the delay once and then trusts it.
 * It does not survive the clock offset being corrected two seconds later, it
 * does not survive a backgrounded tab throttling timers to once a second, and
 * it drifts across a chain of phases because each hop adds its own error.
 *
 * Checking `clock.serverNow() >= deadline` on the render loop we already run
 * has none of those problems: it re-reads the current offset every frame, it
 * fires late (never early) when the tab was asleep, and it costs one
 * comparison per frame.
 */

export type Unschedule = () => void;

/**
 * How far into a phase we are, from its own absolute boundaries.
 *
 * `elapsed = (endsAt - startsAt) - remaining`, where `remaining` came from the
 * synced clock. Returns null for a phase with no scheduled end, which has no
 * meaningful elapsed fraction to report.
 */
export function elapsedInPhase(envelope: PhaseEnvelope, remainingMs: number): number | null {
  if (envelope.endsAt === null) return null;
  return envelope.endsAt - envelope.startsAt - remainingMs;
}

/**
 * Whether a flash that lasts `holdMs` from the start of a phase is still up.
 *
 * The whole point is that this is a *function of the server's timestamps*
 * rather than of a local timer. A `setTimeout` started when the phase packet
 * arrived would keep its own private idea of when to stop — one that survives
 * neither a corrected clock offset nor a backgrounded tab, so a player who
 * switched apps during the countdown could come back to a GO! still sitting
 * over a match that began ten seconds ago.
 */
export function isWithinPhaseHold(
  envelope: PhaseEnvelope,
  remainingMs: number,
  holdMs: number,
): boolean {
  const elapsed = elapsedInPhase(envelope, remainingMs);
  return elapsed !== null && elapsed >= 0 && elapsed < holdMs;
}

/** Run `fn` once the server clock reaches `at`. Fires immediately if already past. */
export function atServerTime(clock: SyncedClock, at: Timestamp, fn: () => void): Unschedule {
  if (clock.until(at) <= 0) {
    fn();
    return () => {};
  }

  let done = false;
  const stop = renderLoop.subscribe(() => {
    if (done || clock.until(at) > 0) return;
    done = true;
    stop();
    fn();
  });

  return () => {
    done = true;
    stop();
  };
}

export interface PhaseHandlers {
  /** The phase became current. */
  onEnter?(envelope: PhaseEnvelope): void;
  /** Called every frame while the phase is running, with ms left (>= 0). */
  onProgress?(remainingMs: number, envelope: PhaseEnvelope): void;
  /** The phase's own deadline passed. The server drives the actual change. */
  onElapsed?(envelope: PhaseEnvelope): void;
  /** Same phase/seq resent after reconnection or with refreshed metadata. */
  onRefresh?(envelope: PhaseEnvelope): void;
}

/**
 * Tracks the current phase and drives countdown UI from it.
 *
 * The client never advances the phase itself — the server owns that, and this
 * only reacts. `onElapsed` firing means "the deadline you were told about has
 * passed", not "we are now in the next phase".
 */
export class PhaseTracker {
  private envelope: PhaseEnvelope | null = null;
  private cancelProgress: Unschedule = () => {};
  private cancelDeadline: Unschedule = () => {};

  constructor(
    private readonly clock: SyncedClock,
    private readonly handlers: PhaseHandlers = {},
  ) {}

  get current(): PhaseEnvelope | null {
    return this.envelope;
  }

  /** Feed in a PHASE message. Out-of-order messages are ignored. */
  set(envelope: PhaseEnvelope): void {
    if (this.envelope && envelope.seq < this.envelope.seq) return;
    if (this.envelope && envelope.seq === this.envelope.seq) {
      this.envelope = envelope;
      this.handlers.onRefresh?.(envelope);
      return;
    }

    this.clear();
    this.envelope = envelope;
    this.handlers.onEnter?.(envelope);

    if (this.handlers.onProgress) {
      const onProgress = this.handlers.onProgress;
      this.cancelProgress = renderLoop.subscribe(() => {
        const end = envelope.endsAt;
        if (end === null) return;
        onProgress(Math.max(0, this.clock.until(end)), envelope);
      });
    }

    if (envelope.endsAt !== null && this.handlers.onElapsed) {
      const onElapsed = this.handlers.onElapsed;
      this.cancelDeadline = atServerTime(this.clock, envelope.endsAt, () => onElapsed(envelope));
    }
  }

  private clear(): void {
    this.cancelProgress();
    this.cancelDeadline();
    this.cancelProgress = () => {};
    this.cancelDeadline = () => {};
  }

  destroy(): void {
    this.clear();
    this.envelope = null;
  }
}
